import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { 
  Home as HomeIcon,
  Folder as FolderIcon,
  Package as PackageIcon,
  BarChart as BarChartIcon,
  User as UserIcon,
  Cog as CogIcon,
  LogOut as LogoutIcon,
  X as XIcon,
  Menu as MenuIcon
} from 'lucide-react';
import { useAuth } from '../contexts';
import { NavigationProps } from '../types/navigation';
import { NavigationLink } from './NavigationLink';
import { getAppVersion } from '../serviceWorkerRegistration';
import OnDemandInstall from './OnDemandInstall';



// Styled components for the custom components
const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TooltipContent = styled.div<{ $placement: string }>`
  position: absolute;
  background-color: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 1000;
  white-space: nowrap;
  
  ${({ $placement }) => {
    switch ($placement) {
      case 'top':
        return css`
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: 4px;
          
        `;
      case 'right':
        return css`
          left: 100%;
          top: 50%;
          transform: translateY(-50%);
          margin-left: 4px;
        `;
      // Add other placements as needed
      default:
        return css`
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-top: 4px;
        `;
    }
  }}
`;

const IconButtonStyled = styled.button<{ $size: string, $color: string }>`
  background: none;
  border: none;
  padding: ${({ $size }) => {
    switch ($size) {
      case 'small': return '4px';
      case 'large': return '12px';
      default: return '8px';
    }
  }};
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  
  color: ${({ theme, $color }) => {
    if ($color === 'primary') return theme.colors.primary;
    if ($color === 'secondary') return theme.colors.secondary;
    return 'inherit';
  }};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary}10;
  }
  
  &:active {
    background-color: ${({ theme }) => theme.colors.primary}20;
  }
`;

// Navigation Items Configuration
const MAIN_NAV_ITEMS = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: HomeIcon,
    requiresAuth: true
  },
  {
    path: '/projects',
    label: 'Projects',
    icon: FolderIcon,
    requiresAuth: true
  },
  {
    path: '/cartons',
    label: 'Cartons',
    icon: PackageIcon,
    requiresAuth: true,
    submenu: [
      {
        path: '/cartons',
        label: 'Dashboard',
        requiresAuth: true
      },
      {
        path: '/cartons/dispatch',
        label: 'Dispatch',
        requiresAuth: true
      },
      {
        path: '/cartons/receiving',
        label: 'Delivery',
        requiresAuth: true
      },
      {
        path: '/cartons/content',
        label: 'Site Receiving',
        requiresAuth: true
      }
    ]
  },
  {
    path: '/reports',
    label: 'Reports',
    icon: BarChartIcon,
    requiresAuth: true
  }
];

const USER_MENU_ITEMS = [
  {
    path: '/profile',
    label: 'Profile',
    icon: UserIcon,
    requiresAuth: true
  },
  {
    path: '/settings',
    label: 'Settings',
    icon: CogIcon,
    requiresAuth: true
  }
];

// Mobile navigation uses the same items as main navigation but with modified submenu for Cartons
const MOBILE_NAV_ITEMS = MAIN_NAV_ITEMS.map(item => {
  if (item.path === '/cartons') {
    return {
      ...item,
      submenu: [
        {
          path: '/cartons',
          label: 'Dashboard',
          requiresAuth: true
        },
        ...(item.submenu || [])
      ]
    };
  }
  return item;
});

// First, define the base NavLink component
const NavLink = styled(Link)<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  color: ${props => props.$isActive ? props.theme.colors.white : props.theme.colors.text};
  text-decoration: none;
  border-radius: 6px;
  transition: all 0.2s ease;
  background: ${props => props.$isActive ? props.theme.colors.primaryLight : 'transparent'};

  &:hover {
    color: ${props => props.theme.colors.white};
    background: ${props => props.theme.colors.primaryLight};
  }
`;

// Then define SubMenuItem that extends NavLink
const SubMenuItem = styled(NavLink)<{ $isActive?: boolean }>`
  padding-left: 2.5rem;
  font-size: 0.875rem;
  color: ${props => props.$isActive ? props.theme.colors.white : props.theme.colors.text};
  background: ${props => props.$isActive ? props.theme.colors.primary : 'transparent'};
  
  &:hover {
    color: ${props => props.theme.colors.white};
    background: ${props => props.theme.colors.primary};
  }
`;

// Styled Components
const NavigationContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 260px;
  height: 100vh;
  background: ${({ theme }) => theme.colors.white};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  z-index: 1000;
  display: flex;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    top: auto;
    width: 100%;
    height: auto;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    border-right: none;
  }
`;

const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[4]};

  &:last-child {
    margin-top: auto;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    padding-top: ${({ theme }) => theme.spacing[4]};
  }
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const NavItem = styled.li<{ $isActive: boolean }>`
  margin: 0;
`;

const SubMenu = styled.div`
  margin-left: ${({ theme }) => theme.spacing[8]};
  margin-top: ${({ theme }) => theme.spacing[1]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const LogoImage = styled.img`
  height: 32px;
  width: auto;
  object-fit: contain;
  transition: opacity 0.2s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    height: 28px;
  }
`;

const LogoText = styled.span`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  transition: all 0.2s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: 1.1rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
    
    ${LogoImage} {
      opacity: 0.9;
    }
    
    ${LogoText} {
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 0.5rem 1rem;
  }
`;

const MobileHeader = styled.div`
  display: none;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 60px;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: flex;
  }
`;

const DesktopNav = styled(NavigationContainer)`
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const MobileNav = styled.div`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 70px;
    background: ${({ theme }) => theme.colors.white};
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    z-index: 1001;
  }
`;

const ConnectionStatus = styled.div<{ $isOnline: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme, $isOnline }) => $isOnline ? theme.colors.successLight : theme.colors.errorLight};
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.typography.sm};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;


// Add new styled components for the mobile menu
const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 85%;
  max-width: 320px;
  height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  z-index: 1002;
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  
  &::after {
    content: 'v${getAppVersion()}';
    display: block;
    text-align: center;
    font-size: 10px;
    color: ${({ theme }) => theme.colors.textSecondary};
    padding: 12px;
    margin-top: auto;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.background};
  }
`;

const MenuOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease;
`;

const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  h2 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: white;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  button {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: white;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: scale(1.05);
    }
  }
`;

const MobileMenuContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.background};
`;

const MobileMenuItem = styled.button`
  width: 100%;
  padding: 1rem;
  text-align: left;
  background: none;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-radius: 8px;
  margin: 0.25rem 0;

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
    transform: translateX(4px);
  }

  &:active {
    background: ${({ theme }) => theme.colors.gray100};
    transform: translateX(2px);
  }
`;

const MobileSubMenu = styled.div`
  padding-left: 2rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: 8px;
  margin: 0.25rem 0;
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
`;

const MobileSubMenuItem = styled(Link)<{ $isActive?: boolean }>`
  padding: 0.75rem 1rem;
  text-align: left;
  background: ${({ theme, $isActive }) => $isActive ? theme.colors.primaryLight : 'none'};
  border: none;
  color: ${({ theme, $isActive }) => $isActive ? theme.colors.primary : theme.colors.text};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  border-radius: 6px;
  margin: 0.25rem 0;

  &:hover {
    background: ${({ theme, $isActive }) => $isActive ? theme.colors.primaryLight : theme.colors.gray50};
    color: ${({ theme, $isActive }) => $isActive ? theme.colors.primary : theme.colors.text};
  }

  &:active {
    background: ${({ theme, $isActive }) => $isActive ? theme.colors.primaryLight : theme.colors.gray100};
  }
`;


const Navigation: React.FC<NavigationProps> = ({ user, isOnline = true }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(() => {
    // Initialize with all submenus collapsed by default
    const initialExpandedMenus: Record<string, boolean> = {};
    MAIN_NAV_ITEMS.forEach(item => {
      if (item.submenu) {
        initialExpandedMenus[item.path] = false;
      }
    });
    return initialExpandedMenus;
  });
  const appVersion = getAppVersion();

  // Toggle menu expansion
  const toggleMenu = (path: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleMobileMenuItemClick = (item: any, e: React.MouseEvent) => {
    if (item.submenu) {
      e.preventDefault();
      toggleMenu(item.path);
    } else {
      setMobileMenuOpen(false);
    }
  };

  const renderMainNavItems = () => (
    <NavList>
      {MAIN_NAV_ITEMS.map((item) => {
        const isActive = location.pathname === item.path;
        const hasSubmenu = item.submenu && item.submenu.length > 0;
        const isSubmenuActive = hasSubmenu && item.submenu.some(subItem => 
          location.pathname === subItem.path
        );
        const isExpanded = expandedMenus[item.path] || isSubmenuActive;

        return (
          <NavItem key={item.path} $isActive={!!(isActive || isSubmenuActive)}>
            <NavLink
              to={item.path}
              $isActive={isActive || isSubmenuActive}
              onClick={(e) => {
                if (hasSubmenu) {
                  e.preventDefault();
                  toggleMenu(item.path);
                }
              }}
            >
              <item.icon />
              {item.label}
              {hasSubmenu && (
                <span style={{ 
                  marginLeft: 'auto',
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)',
                  transition: 'transform 0.2s ease',
                  display: 'inline-flex'
                }}>
                  {'▶'}
                </span>
              )}
            </NavLink>
            {hasSubmenu && isExpanded && (
              <SubMenu>
                {item.submenu.map((subItem) => (
                  <SubMenuItem
                    key={subItem.path}
                    to={subItem.path}
                    $isActive={location.pathname === subItem.path}
                  >
                    {subItem.label}
                  </SubMenuItem>
                ))}
              </SubMenu>
            )}
          </NavItem>
        );
      })}
    </NavList>
  );

  const renderUserMenuItems = () => (
    <NavList>
      {USER_MENU_ITEMS.map((item) => (
        <NavItem key={item.path} $isActive={location.pathname === item.path}>
          <NavLink 
            to={item.path} 
            $isActive={location.pathname === item.path}
          >
            <item.icon />
            {item.label}
          </NavLink>
        </NavItem>
      ))}
        <NavItem $isActive={false}>
          <button onClick={handleLogout} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            padding: '0.75rem 1rem', 
            color: 'inherit', 
            textDecoration: 'none', 
            borderRadius: '6px', 
            transition: 'all 0.2s ease', 
            background: 'transparent', 
            border: 'none', 
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left'
          }}>
            <LogoutIcon />
            Logout
          </button>
        </NavItem>
    </NavList>
  );

  const renderMobileMenuItem = (item: any) => {
    if (!item || !item.path) return null;
    
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isSubmenuActive = hasSubmenu && item.submenu.some((subItem: any) => 
      location.pathname === subItem.path
    );
    const isExpanded = expandedMenus[item.path] || false;

    return (
      <React.Fragment key={item.path}>
        {hasSubmenu ? (
          <MobileMenuItem 
            onClick={(e) => handleMobileMenuItemClick(item, e)}
            style={{
              background: isSubmenuActive ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'transparent',
              color: isSubmenuActive ? '#fff' : 'inherit',
              fontWeight: '600'
            }}
          >
            {item.icon && <item.icon size={20} />}
            <span>{item.label}</span>
            <span style={{ 
              marginLeft: 'auto',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)',
              transition: 'transform 0.2s ease',
              fontSize: '0.8rem',
              display: 'inline-flex',
              alignItems: 'center'
            }}>
              {'▶'}
            </span>
          </MobileMenuItem>
        ) : (
          <Link to={item.path} onClick={() => setMobileMenuOpen(false)} style={{ textDecoration: 'none' }}>
            <MobileMenuItem>
              {item.icon && <item.icon size={20} />}
              <span>{item.label}</span>
            </MobileMenuItem>
          </Link>
        )}
        
        {hasSubmenu && isExpanded && (
          <MobileSubMenu>
            {item.submenu.map((subItem: any) => (
              <MobileSubMenuItem
                key={subItem.path}
                to={subItem.path}
                $isActive={location.pathname === subItem.path}
                onClick={() => setMobileMenuOpen(false)}
              >
                {subItem.label}
              </MobileSubMenuItem>
            ))}
          </MobileSubMenu>
        )}
      </React.Fragment>
    );
  };

  if (!user) {
    return null;
  }

  return (
    <>
      {/* Mobile Header */}
      <MobileHeader>
        <Logo to="/dashboard">
          <LogoImage 
            src="/images/logo-mini.png" 
            alt="FMS Logo"
            className="mobile-logo"
          />
          <LogoText>FMS</LogoText>
        </Logo>
        <button 
          onClick={() => setMobileMenuOpen(true)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <MenuIcon size={24} />
        </button>
      </MobileHeader>

      {/* Desktop Navigation */}
      <DesktopNav>
        <NavSection>
          <Logo to="/dashboard">
            <LogoImage 
              src="/images/logo-ex.png" 
              alt="FMS Logo"
              className="desktop-logo"
            />
            <LogoText className="desktop-logo-text">FMS</LogoText>
          </Logo>
          {isOnline ? (
            <ConnectionStatus $isOnline={isOnline}>
              <span>Online</span>
            </ConnectionStatus>
          ) : (
            <ConnectionStatus $isOnline={isOnline}>
              <span>Offline</span>
            </ConnectionStatus>
          )}
          
          {renderMainNavItems()}
      
        </NavSection>
        <NavSection>
          {renderUserMenuItems()}
          <div style={{ padding: '0 16px', marginTop: 'auto', marginBottom: '16px', fontSize: '12px', color: 'var(--text-secondary)' }}>
            v{appVersion}
          </div>
        </NavSection>
      </DesktopNav>

      {/* Mobile Bottom Navigation */}
      <MobileNav>
        <NavList style={{ flexDirection: 'row', justifyContent: 'space-around', width: '100%' }}>
          {MOBILE_NAV_ITEMS.map((item) => {
            if (!item.path || !item.icon || !item.label) return null;
            return (
              <NavItem key={item.path} $isActive={location.pathname === item.path}>
                <NavigationLink
                  to={item.path}
                  icon={item.icon}
                  label={item.label}
                  isActive={location.pathname === item.path}
                  isMobile
                />
              </NavItem>
            );
          })}
        </NavList>
      </MobileNav>

      {/* Mobile Menu Overlay */}
      <MenuOverlay 
        $isOpen={mobileMenuOpen}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <MobileMenu $isOpen={mobileMenuOpen}>
        <MobileMenuHeader>
          <h2>Menu</h2>
          <button onClick={() => setMobileMenuOpen(false)}>
            <XIcon size={24} />
          </button>
        </MobileMenuHeader>
        
        <MobileMenuContent>
          {MAIN_NAV_ITEMS.map(renderMobileMenuItem)}
          {USER_MENU_ITEMS.map(renderMobileMenuItem)}
          
          {/* PWA Install Button */}
          <div style={{ 
            padding: '16px', 
            borderTop: '1px solid #e5e7eb',
            marginTop: '8px'
          }}>
            <OnDemandInstall 
              variant="primary"
              showStatus={true}
              showQuickGuide={true}
            />
          </div>
          
          <MobileMenuItem as="button" onClick={handleLogout}>
            <LogoutIcon size={20} />
            Logout
          </MobileMenuItem>          
        </MobileMenuContent>
      </MobileMenu>
    </>
  );
};


export default Navigation;